import { useMobileDetection } from '@common/utils'
import {
  MolsonBox,
  MolsonButton,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import { IconButton, useMediaQuery } from '@mui/material'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { usePwaInstallPromptUtils } from './pwaInstallUtils'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const PWA_BANNER_DISMISSED_TIMESTAMP = 'pwa-install-banner-dismissed-time'
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 days

export const PwaInstallBanner = () => {
  const { t } = useTranslation('', { keyPrefix: 'common.pwa' })
  const theme = molsonUseTheme()
  const { isAuthenticated } = useAuth()
  const { isMobile, isTablet } = useMobileDetection()
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [isIos, setIsIos] = useState(false)
  const [showIosInstructions, setShowIosInstructions] = useState(false)
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')

  // Only show on mobile/tablet devices and before user is authenticated
  const isMobileOrTablet = isMobile || isTablet

  useEffect(() => {
    // Don't show banner on desktop devices
    if (!isMobileOrTablet) {
      return
    }

    // Don't show banner after user is authenticated
    if (isAuthenticated) {
      return
    }

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isIosStandalone = (window.navigator as any).standalone === true

    if (isStandalone || isIosStandalone) {
      return
    }

    // Check if banner was dismissed recently
    const dismissedTimestamp = localStorage.getItem(
      PWA_BANNER_DISMISSED_TIMESTAMP
    )
    if (dismissedTimestamp) {
      const timeSinceDismiss = Date.now() - Number.parseInt(dismissedTimestamp)
      if (timeSinceDismiss < DISMISS_DURATION) {
        return
      }
    }

    // Detect iOS
    const isIosDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    setIsIos(isIosDevice)

    // For iOS, show banner if not installed
    if (isIosDevice) {
      setShowBanner(true)
      return
    }

    // For Chrome/Edge Android - listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowBanner(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      )
    }
  }, [isMobileOrTablet, isAuthenticated])

  const handleInstallClick = async () => {
    if (isIos) {
      setShowIosInstructions(true)
      return
    }

    /* v8 ignore next */
    if (!deferredPrompt) {
      return
    }

    // Show the install prompt
    await deferredPrompt.prompt()

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setShowBanner(false)
    }

    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    localStorage.setItem(PWA_BANNER_DISMISSED_TIMESTAMP, Date.now().toString())
    setShowBanner(false)
  }

  const { showPromptForIos } = usePwaInstallPromptUtils({
    setShowIosInstructions,
    handleDismiss
  })

  // Don't show banner on desktop or when user is authenticated
  if (!(showBanner && isMobileOrTablet) || isAuthenticated) {
    return null
  }

  if (showIosInstructions) {
    return showPromptForIos()
  }

  return (
    <MolsonBox
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        p: 2,
        bgcolor: prefersDarkMode
          ? theme.palette.secondary[700]
          : theme.palette.secondary[100],
        boxShadow: `0 -4px 12px ${theme.palette.primary.lightShadow}`
      }}
    >
      <MolsonBox
        sx={{
          maxWidth: 600,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <img
          src="/assets/icons/icon-72x72.png"
          alt="PriceCMD"
          style={{
            width: 48,
            height: 48,
            borderRadius: 2,
            flexShrink: 0
          }}
        />

        <MolsonBox
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}
        >
          <MolsonTypography
            sx={{
              fontWeight: 'bold',
              fontSize: 17,
              lineHeight: 1.2,
              color: prefersDarkMode
                ? theme.palette.secondary[50]
                : theme.palette.secondary[900]
            }}
          >
            {t('installApp')}
          </MolsonTypography>
          <MolsonTypography
            sx={{
              fontSize: 13,
              lineHeight: 1.2,
              color: prefersDarkMode
                ? theme.palette.secondary[400]
                : theme.palette.secondary[500]
            }}
          >
            {isIos ? t('iosSubtitle') : t('androidSubtitle')}
          </MolsonTypography>
        </MolsonBox>

        <MolsonBox sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <MolsonButton
            variant="contained"
            onClick={handleInstallClick}
            sx={{
              borderRadius: '40px',
              fontSize: 15,
              p: 0,
              paddingInline: 1,
              height: 30
            }}
          >
            {t('installButton')}
          </MolsonButton>
          <IconButton
            onClick={handleDismiss}
            sx={{
              color: prefersDarkMode
                ? theme.palette.secondary[50]
                : theme.palette.secondary[500]
            }}
          >
            <MolsonIcon variant="CLOSE_X" />
          </IconButton>
        </MolsonBox>
      </MolsonBox>
    </MolsonBox>
  )
}
